import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { IApiError, IListData, IOrderParameter } from '@models';
import { AuthService } from '@services';
import { catchArrayBufferErrors, checkErrors } from '@shared';
import * as _ from 'lodash';
import * as moment from 'moment';
import { forkJoin, from, map, Observable } from 'rxjs';
import { InvoiceRegister } from '../../models/invoice/invoice-register.model';
import { ConfigService } from '../config.service';
import { IInvoiceRegisterFilterParam } from '../invoices/iapi/iinvoice-register.api';
import { NoRestApiHelper } from '../no-rest-api.helper';
import { PrintService } from '../print/print.service';
import { FailedObject, PartialSuccessResponse } from '../../shared/components/partial-success/partial-success.model';
import {
  IRegisterCreateResponse,
  IRegisterDeleteResponse,
  IRegisterErrorItem,
  IRegisterSuccessItem,
  IRegisterWarningItem,
} from '../../models/register.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceRegisterApiService {
  win: Window;

  constructor(
    protected http: HttpClient,
    protected configService: ConfigService,
    protected _noRestApiHelper: NoRestApiHelper,
    protected _authorizationService: AuthService,
    private printService: PrintService,
    private injector: Injector,
  ) {}

  getList(filter: IInvoiceRegisterFilterParam = { isOpen: true },
    order: IOrderParameter, page = 1, limit = 10000): Observable<IListData<InvoiceRegister>> {

    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        modelName: 'ScanSheet',
        system: this.configService.get('system'),
        calledMethod: 'getScanSheetList',
        methodProperties: {
          Open: filter.isOpen ? '1' : undefined,
          Page: page,
          Limit: limit,
          SearchByCounterparties: this._authorizationService.user.contacts ? 1 : null,
        },
      });

    return observable.pipe(
        map(data => this._noRestApiHelper.checkErrors(data)),
        map(data => this.bindListToModel(data)),
      );
  }

  getOne(id: string): Observable<InvoiceRegister> {
    const obs: any = this.http.post(`${this.configService.get('apiUrl')}json/`, {
      system: this.configService.get('system'),
      modelName: 'ScanSheet',
      calledMethod: 'getScanSheet',
      methodProperties: {
        Ref: id,
      },
    });

    return obs.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector)),
      map(data => this._bindOneToModel(data)),
    );
  }

  remove(id: string): Observable<any> {
    const observable: any = this.http.post(`${this.configService.get('apiUrl')}json/`, {
      modelName: 'ScanSheet',
      calledMethod: 'deleteScanSheet',
      methodProperties: {
        ScanSheetRefs: [id],
      },
    });

    //noinspection TypeScriptUnresolvedFuncti on
    return from(observable).pipe(
      map(data => this._noRestApiHelper.checkErrors({data})),
      map(data => checkErrors(data, this.injector)),
      map(data => data.data),
    );
  }

  removeList(ids: string[]): Observable<boolean> {
    const removed: Observable<boolean>[] = [];
    _.each(ids, (id) => {
      removed.push(this.remove(id));
    });

    return forkJoin(removed).pipe(map(r => r.every(done => !!done)));
  }

  printDocuments(invoicesIds: string[]): Observable<any> {
    const printDocumentsPromise: any = this.http.post(
      `${this.configService.get('apiUrl')}json/`,
      {
        system: this.configService.get('system'),
        modelName: 'ScanSheet',
        calledMethod: 'printScanSheet',
        methodProperties: {
          Ref: invoicesIds,
          Type: 'pdf',
        },
      },
      { responseType: 'arraybuffer' },
    );

    return from(printDocumentsPromise).pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(response => response.data),
    );
  }

  printDocumentsNew(invoicesIds: string[], type: string = 'pdf', orientation: string): Observable<any> {
    return this.http
      .post(
        `${this.configService.get('apiUrl')}json/`,
        {
          system: this.configService.get('system'),
          modelName: 'InternetDocument',
          calledMethod: 'printFull',
          methodProperties: {
            printForm: 'ScanSheet',
            ScanSheetRefs: invoicesIds,
            Type: type,
            PrintOrientation: orientation,
          },
        },
        { responseType: 'arraybuffer' },
      )
      .pipe(
        map(response => catchArrayBufferErrors(response)),
        map(data => this._noRestApiHelper.checkErrors({ data })),
        map(response => response.data),
        map(data => {
          this.printService.openBlob(data, type);
          return null;
        }),
      );
  }


  printMarking(invoicesIds: string[], type: string = 'pdf'): Observable<any> {
    const printDocumentsPromise: any = this.http.post(
      `${this.configService.get('apiUrl')}json/`,
      {
        system: this.configService.get('system'),
        modelName: 'InternetDocument',
        calledMethod: 'printFull',
        methodProperties: {
          printForm: 'Marking',
          DocumentRefs: invoicesIds,
          Type: type,
          Position: '',
        },
      },
      { responseType: 'arraybuffer' },
    );

    return from(printDocumentsPromise).pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(response => response.data),
      map(data => {
        this.printService.openBlob(data, type);
        return null;
      }),
    );
  }

  addWithInvoices(description: string, idInvoices: string[], date?: Date): Observable<PartialSuccessResponse> {
    const request =  this.http.post(`${this.configService.get('apiUrl')}json/`, {
      system: this.configService.get('system'),
      modelName: 'ScanSheet',
      calledMethod: 'insertDocuments',
      methodProperties: {
        Description: description,
        DocumentRefs: idInvoices,
        SearchByCounterparties: this._authorizationService.user.contacts ? 1 : null,
        Date: moment(date).format('DD.MM.YYYY'),
        iCounterparties: this._authorizationService.user.contacts
          ? this._authorizationService.user.contacts.map(contact => contact.CounterpartyRef)
          : null,
      },
    });
    return request.pipe(
        map(data => this._noRestApiHelper.checkErrors({ data })),
        map(response => this.parseInsertResponseForPartialSuccess(response.data.data[0])),
      );
  }

  update(item: InvoiceRegister): Observable<PartialSuccessResponse> {
    const observable: any = this.http.post(`${this.configService.get('apiUrl')}json/`, {
      system: this.configService.get('system'),
      modelName: 'ScanSheet',
      calledMethod: 'updateScanSheet',
      methodProperties: {
        Ref: item.Ref,
        Description: item.Description,
      },
    });

    return from(observable).pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector)),
      map(data => data.data.success),
    );
  }

  insertDocuments(idRegister: string, idInvoices: string[]): Observable<PartialSuccessResponse> {
    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'ScanSheet',
        calledMethod: 'insertDocuments',
        methodProperties: {
          Ref: idRegister,
          DocumentRefs: idInvoices,
        },
      })
      .pipe(
        map(data => this._noRestApiHelper.checkErrors({ data })),
        map(response => this.parseInsertResponseForPartialSuccess(response.data.data[0])),
      );
  }

  createEmptyRegister(date?: Date, Description?: string): Observable<InvoiceRegister> {
    const insertPromise = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'ScanSheet',
        calledMethod: 'createEmptyScanSheet',
        methodProperties: {
          Date: moment(date || new Date()).format('DD.MM.YYYY'),
          Description,
        },
      });

    return from(insertPromise).pipe(
      map(data => this._noRestApiHelper.checkErrors(data)),
      map(data => this._bindOneToModel(data)),
    );
  }

  deleteInvoices(idRegister: string, idsInvoices: string[]): Observable<PartialSuccessResponse> {
    const promise: any = this.http.post(`${this.configService.get('apiUrl')}json/`, {
      system: this.configService.get('system'),
      modelName: 'ScanSheet',
      calledMethod: 'removeDocuments',
      methodProperties: {
        Ref: idRegister,
        DocumentRefs: idsInvoices,
      },
    });
    return from(promise).pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.parseRemoveResponseForPartialSuccess(data.data)),
    );
  }

  private parseInsertResponseForPartialSuccess(response: IRegisterCreateResponse): PartialSuccessResponse | null {
    if (!response) {
      return null;
    }
    const data = response;

    const responseDataErrors: IRegisterErrorItem[] = data.Data?.Errors ?? [];
    const responseDataWarnings: IRegisterWarningItem[] = data.Data?.Warnings ?? [];

    if (responseDataErrors.length === 0 && responseDataWarnings.length === 0) {
      return {
        Barcode: data.Number,
        succeeded: data.Data.Success.map(el => el.Number),
        failed: [],
        total: {
          succeededAmount: data.Data.Success.length,
          failedAmount: 0,
          totalAmount: data.Data.Success.length,
        },
      };
    }

    const failed: FailedObject[] = [...responseDataErrors, ...responseDataWarnings]
      .map((el: IRegisterErrorItem | IRegisterWarningItem) => ({
        number: (el as IRegisterWarningItem).Number || (el as IRegisterErrorItem).Ref,
        errorMessage: (el as IRegisterWarningItem).Warning || (el as IRegisterErrorItem).Error,
      }));

    return {
      Barcode: data.Number,
      Ref: data.Ref,
      succeeded: data.Success.map(el => el.Number),
      failed,
      total: {
        succeededAmount: data.Success.length,
        failedAmount: failed.length,
        totalAmount: data.Success.length + failed.length,
      },
    };
  }

  private parseRemoveResponseForPartialSuccess(response: IRegisterDeleteResponse): PartialSuccessResponse | null {
    if (!response) {
      return null;
    }

    const responseDataErrors: IRegisterErrorItem[] = response.data.DocumentRefs.Errors ?? [];
    const success: IRegisterSuccessItem[] = response.data.DocumentRefs.Success ?? [];

    if (responseDataErrors.length === 0) {
      return {
        failed: [],
        total: {
          succeededAmount: success.length,
          failedAmount: 0,
          totalAmount: success.length + responseDataErrors.length,
        },
      };
    }

    const failed: FailedObject[] = [...responseDataErrors]
      .map((el: IRegisterErrorItem) => ({
        number: el.Number,
        errorMessage: el.Error,
      }));

    return {
      failed,
      total: {
        succeededAmount: success.length,
        failedAmount: failed.length,
        totalAmount: success.length + failed.length,
      },
    };
  }

  bindListToModel(data: any): IListData<InvoiceRegister> {
    return {
      list: _.map(data.data, (el: any) => {
        const invoiceRegister = new InvoiceRegister(el.Ref);
        invoiceRegister.docNumber = el.Number;
        invoiceRegister.printed = el.Printed;
        invoiceRegister.dateTime = el.DateTime ? moment(el.DateTime).toDate() : null;
        invoiceRegister.description = el.Description;
        invoiceRegister.marketplacePartnerDescription = el.MarketplacePartnerDescription;
        invoiceRegister.count = el.Count;
        return invoiceRegister;
      }),
      total: data.info.totalCount,
    };
  }

  protected _bindOneToModel(response: any): InvoiceRegister {
    let invoiceRegister: InvoiceRegister = null;
    if (response.success) {
      const item: any = response.data[0];

      if (item) {
        invoiceRegister = new InvoiceRegister(item.Ref);
        invoiceRegister.docNumber = item.Number;
        invoiceRegister.ref = item.Ref;
        invoiceRegister.printed = item.Printed || false;
        invoiceRegister.dateTime = new Date(item.DateTime);
        invoiceRegister.description = item.Description;
        invoiceRegister.marketplacePartnerDescription = item.MarketplacePartnerDescription;
        invoiceRegister.citySender = item.CitySender;
        invoiceRegister.citySenderRef = item.CitySenderRef;
        invoiceRegister.sender = item.Sender;
        invoiceRegister.senderRef = item.SenderRef;
        invoiceRegister.senderAddress = item.SenderAddress;
        invoiceRegister.senderAddressRef = item.SenderAddressRef;
        invoiceRegister.count = item.Count || 0;
        invoiceRegister.weight = item.Weight || 0;
        invoiceRegister.totalCost = item.TotalCost || 0;
        invoiceRegister.totalRedeliverySum = item.TotalRedeliverySum || 0;
        invoiceRegister.totalAfterpaymentOnGoodsCost = item.TotalAfterpaymentOnGoodsCost || 0;
        invoiceRegister.TotalCheckCost = Number(item.TotalCheckCost) ? item.TotalCheckCost : 0;
        invoiceRegister.TotalCheckCostSender = Number(item.TotalCheckCostSender) ? item.TotalCheckCostSender : 0;
        invoiceRegister.TotalCheckWeight = Number(item.TotalCheckWeight) ? item.TotalCheckWeight : 0;
        invoiceRegister.TotalCost = Number(item.TotalCost) ? item.TotalCost : 0;
        invoiceRegister.TotalCostOnSite = Number(item.TotalCostOnSite) ? item.TotalCostOnSite : 0;
        invoiceRegister.TotalCostOnSiteSender = Number(item.TotalCostOnSiteSender) ? item.TotalCostOnSiteSender : 0;
        invoiceRegister.TotalRedeliverySum = Number(item.TotalRedeliverySum) ? item.TotalRedeliverySum : 0;
        invoiceRegister.TotalWeight = Number(item.TotalWeight) ? item.TotalWeight : 0;
        invoiceRegister.TotalAfterpaymentOnGoodsCost = Number(item.TotalAfterpaymentOnGoodsCost) ? item.TotalAfterpaymentOnGoodsCost : 0;
        invoiceRegister.Count = item.Count || 0;
        invoiceRegister.Sender = item.Sender;
        invoiceRegister.SenderAddress = item.SenderAddress;
      }
    } else {
      const errors: IApiError[] = [];
      _.each(response.errorCodes, (el, key) => {
        errors.push({
          messageCode: el,
          errorCode: this._noRestApiHelper.replaceErrorCodeOnCustom(el),
          message: response.data.errors[key],
        });
      });
      throw errors;
    }
    return invoiceRegister;
  }
}
